import React, { useRef, useState } from 'react';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Bolt, BoltOff, Camera, X } from 'tabler-icons-react';
import useBarcodeScanner, { ERRORES, ESTADOS } from '../../hooks/useBarcodeScanner';
import { decodificarImagen } from './decodificarImagen';
import '../../styles/barcodeScanner.css';

const ESTADO_FOTO = {
    INICIAL: 'inicial',
    DECODIFICANDO: 'decodificando',
    FALLO: 'fallo',
};

// Vía de respaldo cuando el navegador no permite cámara en vivo (contexto
// inseguro: getUserMedia no existe fuera de HTTPS/localhost). Un input de
// archivo con `capture` sí abre la cámara nativa en HTTP — la foto resultante
// se decodifica con ZXing en decodificarImagen.js.
function ModoFoto({ patron, onDetectado, ayuda }) {
    const inputRef = useRef(null);
    const [estado, setEstado] = useState(ESTADO_FOTO.INICIAL);

    const handleArchivo = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setEstado(ESTADO_FOTO.DECODIFICANDO);
        try {
            const codigo = await decodificarImagen(file, patron);
            if (codigo) {
                onDetectado(codigo);
                return;
            }
            setEstado(ESTADO_FOTO.FALLO);
        } catch (e2) {
            setEstado(ESTADO_FOTO.FALLO);
        }
    };

    return (
        <div className="barcode-scanner-foto">
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="barcode-scanner-foto-input"
                onChange={handleArchivo}
            />
            <div className="barcode-scanner-foto-panel">
                {estado === ESTADO_FOTO.DECODIFICANDO ? (
                    <>
                        <CircularProgress size={28} />
                        <p className="barcode-scanner-foto-texto">Leyendo código…</p>
                    </>
                ) : (
                    <>
                        <Camera size={32} strokeWidth={1.5} />
                        {estado === ESTADO_FOTO.FALLO && (
                            <p className="barcode-scanner-foto-texto barcode-scanner-foto-texto--error">
                                No se pudo leer el código en la foto. También puedes escribir el número
                                de serie directamente en el campo.
                            </p>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            className="barcode-scanner-foto-boton"
                            onClick={() => inputRef.current?.click()}
                        >
                            {estado === ESTADO_FOTO.FALLO ? 'Tomar otra foto' : 'Tomar foto'}
                        </Button>
                    </>
                )}
            </div>
            <p className="barcode-scanner-ayuda">{ayuda}</p>
        </div>
    );
}

/**
 * Modal de lectura de código de barras/VIN vía cámara. Sigue el patrón
 * `.usuarios-modal` del resto de la app (ver CalidadFormModal). Toda la
 * cámara vive en useBarcodeScanner; este componente solo la presenta.
 *
 * En contexto inseguro (HTTP) getUserMedia no existe, así que el modal cae a
 * ModoFoto en vez de mostrar un error sin salida.
 */
export default function BarcodeScanner({ open, onClose, onDetectado, patron, titulo, ayuda }) {
    const { videoRef, estado, error, errorCodigo, linternaDisponible, linternaEncendida, alternarLinterna } =
        useBarcodeScanner({ activo: open, patron, onDetectado });

    const modoFoto = errorCodigo === ERRORES.CONTEXTO_INSEGURO;

    return (
        <Modal open={open} onClose={onClose}>
            <div className="usuarios-modal barcode-scanner-modal">
                <div className="usuarios-modal-header">
                    <h2 className="usuarios-modal-title">{titulo || 'Escanear código'}</h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>

                <div className="barcode-scanner-body">
                    {modoFoto ? (
                        <ModoFoto
                            patron={patron}
                            onDetectado={onDetectado}
                            ayuda={ayuda || 'Encuadra el código de barras del sticker, de cerca y con buena luz.'}
                        />
                    ) : estado === ESTADOS.ERROR ? (
                        <div className="barcode-scanner-error">{error}</div>
                    ) : (
                        <>
                            <div className="barcode-scanner-video-wrap">
                                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                <video ref={videoRef} className="barcode-scanner-video" autoPlay playsInline muted />
                                <div className="barcode-scanner-guide" />
                                {estado === ESTADOS.INICIANDO && (
                                    <div className="barcode-scanner-status">Iniciando cámara…</div>
                                )}
                                {linternaDisponible && (
                                    <IconButton
                                        size="small"
                                        className={`barcode-scanner-torch${
                                            linternaEncendida ? ' barcode-scanner-torch--on' : ''
                                        }`}
                                        onClick={alternarLinterna}
                                    >
                                        {linternaEncendida ? (
                                            <Bolt size={20} strokeWidth={2} />
                                        ) : (
                                            <BoltOff size={20} strokeWidth={2} />
                                        )}
                                    </IconButton>
                                )}
                            </div>
                            <p className="barcode-scanner-ayuda">
                                {ayuda || 'Apunta la cámara al código de barras del número de serie.'}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
}
